import React, { useState, useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

interface ParticleData {
  dir: THREE.Vector3;
  dist: number;
  speed: number;
}

function ParticleHalo({ coolingAge, starColor }: { coolingAge: number; starColor: string }) {
  const count = 200;

  // Initialize random particle directions and speeds
  const particles = useMemo(() => {
    const data: ParticleData[] = [];
    for (let i = 0; i < count; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      const dx = Math.sin(phi) * Math.cos(theta);
      const dy = Math.sin(phi) * Math.sin(theta);
      const dz = Math.cos(phi);
      
      const dist = 0.8 + Math.random() * 2.2;
      data.push({
        dir: new THREE.Vector3(dx, dy, dz),
        dist,
        speed: 0.15 + Math.random() * 0.25
      });
    }
    return data;
  }, []);

  const posArray = useMemo(() => new Float32Array(count * 3), []);
  const colorArray = useMemo(() => new Float32Array(count * 3), []);

  const pointsRef = useRef<THREE.Points>(null);

  useFrame((state, delta) => {
    const speedMultiplier = (100 - coolingAge) / 100;
    const c = new THREE.Color(starColor);

    particles.forEach((p, i) => {
      // Drifts outward from star surface
      p.dist += p.speed * speedMultiplier * delta * 2.5;

      // Respawn at star surface if drifted too far
      if (p.dist > 3.0) {
        p.dist = 0.8;
      }

      posArray[i * 3] = p.dir.x * p.dist;
      posArray[i * 3 + 1] = p.dir.y * p.dist;
      posArray[i * 3 + 2] = p.dir.z * p.dist;

      colorArray[i * 3] = c.r;
      colorArray[i * 3 + 1] = c.g;
      colorArray[i * 3 + 2] = c.b;
    });

    if (pointsRef.current) {
      pointsRef.current.geometry.attributes.position.needsUpdate = true;
      pointsRef.current.geometry.attributes.color.needsUpdate = true;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[posArray, 3]} />
        <bufferAttribute attach="attributes-color" args={[colorArray, 3]} />
      </bufferGeometry>
      <pointsMaterial vertexColors size={0.05} sizeAttenuation transparent opacity={0.65} />
    </points>
  );
}

function StarsBackground() {
  const count = 1000;
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const radius = 15 + Math.random() * 15;
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      arr[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = radius * Math.cos(phi);
    }
    return arr;
  }, []);

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#FFFFFF" size={0.035} sizeAttenuation transparent opacity={0.5} />
    </points>
  );
}

function WhiteDwarfStar({ starColor, emissiveIntensity }: { starColor: string; emissiveIntensity: number }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.1;
      meshRef.current.rotation.x = state.clock.getElapsedTime() * 0.05;
    }
  });

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[0.8, 32, 32]} />
      <meshStandardMaterial
        color={starColor}
        emissive={starColor}
        emissiveIntensity={emissiveIntensity}
        roughness={0.3}
        metalness={0.1}
      />
    </mesh>
  );
}

// Handler inside Canvas to run auto-cool ticking smoothly
function AutoCoolTicker({
  autoCool,
  setCoolingAge
}: {
  autoCool: boolean;
  setCoolingAge: React.Dispatch<React.SetStateAction<number>>;
}) {
  useFrame((state, delta) => {
    if (autoCool) {
      setCoolingAge((prev) => {
        const next = prev + delta * 2.0;
        return next >= 100 ? 0 : next;
      });
    }
  });
  return null;
}

export function WhiteDwarf3D() {
  const [coolingAge, setCoolingAge] = useState<number>(0);
  const [autoCool, setAutoCool] = useState<boolean>(true);

  // Color interpolation formulas
  const starColor = useMemo(() => {
    const parseHex = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    const formatHex = (r: number, g: number, b: number) => {
      return `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1)}`;
    };

    const interpolate = (color1: string, color2: string, factor: number) => {
      const c1 = parseHex(color1);
      const c2 = parseHex(color2);
      const r = c1.r + (c2.r - c1.r) * factor;
      const g = c1.g + (c2.g - c1.g) * factor;
      const b = c1.b + (c2.b - c1.b) * factor;
      return formatHex(r, g, b);
    };

    if (coolingAge <= 20) {
      const factor = coolingAge / 20;
      return interpolate('#FFFFFF', '#FEFCE8', factor);
    } else if (coolingAge <= 50) {
      const factor = (coolingAge - 20) / 30;
      return interpolate('#FDE68A', '#F97316', factor);
    } else if (coolingAge <= 80) {
      const factor = (coolingAge - 50) / 30;
      return interpolate('#F97316', '#991B1B', factor);
    } else {
      const factor = (coolingAge - 80) / 20;
      return interpolate('#991B1B', '#1C0A00', factor);
    }
  }, [coolingAge]);

  const emissiveIntensity = useMemo(() => {
    return 3.0 - (coolingAge / 100) * 2.9; // 3 to 0.1
  }, [coolingAge]);

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <Canvas camera={{ position: [0, 2, 4.5], fov: 45 } as any}>
        <color attach="background" args={['#0F172A']} />
        
        <ambientLight intensity={0.05} />
        <pointLight position={[0, 0, 0]} color={starColor} intensity={emissiveIntensity * 2} decay={1.5} distance={15} />

        <StarsBackground />
        <WhiteDwarfStar starColor={starColor} emissiveIntensity={emissiveIntensity} />
        <ParticleHalo coolingAge={coolingAge} starColor={starColor} />
        
        <AutoCoolTicker autoCool={autoCool} setCoolingAge={setCoolingAge} />
        <OrbitControls enableZoom={true} enablePan={false} />
      </Canvas>

      {/* Top Left Stats Overlay */}
      <div className="absolute top-3 left-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-0.5 pointer-events-none select-none">
        <span className="text-[10px] text-white/40 tracking-wider uppercase font-bold">Stellar Cooling State</span>
        <span className="text-white text-sm font-bold">
          Age: {(coolingAge / 10).toFixed(1)} Billion Years
        </span>
        <span className="text-cyan-400 text-xs font-semibold">
          Surface Temp: ~{Math.max(3000, Math.round(100000 * (1 - coolingAge / 100))).toLocaleString()}K
        </span>
      </div>

      {/* Top Right Auto Cool Checkbox */}
      <div className="absolute top-3 right-3 bg-[#0b0f19]/80 backdrop-blur-sm px-3 py-2 rounded-lg border border-white/10 flex items-center gap-2">
        <input
          type="checkbox"
          id="auto-cool"
          checked={autoCool}
          onChange={(e) => setAutoCool(e.target.checked)}
          className="accent-cyan-400 cursor-pointer w-3.5 h-3.5 rounded"
        />
        <label htmlFor="auto-cool" className="text-white text-xs font-bold cursor-pointer select-none">
          Auto Cool
        </label>
      </div>

      {/* Time Scrubber at the bottom center */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1.5">
        <div className="flex justify-between items-center text-white text-[11px] font-bold tracking-wide opacity-80">
          <span>0 (Hot White Dwarf)</span>
          <span className="text-cyan-400 text-xs font-extrabold uppercase tracking-wide">Cooling Timeline</span>
          <span>10 Billion Years (Black Dwarf)</span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="0.5"
          value={coolingAge}
          onChange={(e) => {
            setCoolingAge(parseFloat(e.target.value));
            setAutoCool(false);
          }}
          className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
        />
      </div>
    </div>
  );
}
